import { Badge } from '@fluentui/react-components';

import { ReadinessStatus } from '../api/migrations';

// Maps a preflight readiness verdict (FD-ADR-027) to a Fluent Badge.
//
// Deliberately NOT an extension of StatusBadge: that component maps the device
// LIFECYCLE vocabulary (pending → initialized → in-progress → the four FA-ADR-021
// terminal outcomes), and readiness is a different axis with its own four values.
// Folding them together would mean one component whose colour meaning depends on
// which column it is in.
//
//   pass         -> success     (green) — fit to migrate
//   warn         -> informative (sky)   — evaluated, non-blocking concerns
//   blocked      -> danger      (red)   — a check definitely failed
//   unknown      -> warning     (amber) — a blocking check could not be evaluated
//   not evaluated-> subtle      (zinc)  — no manifest submitted yet
//
// Why amber for `unknown` rather than red: both `blocked` and `unknown` hold a
// device under `enforce`, but they need different operator actions. `blocked` means
// remediate the device; `unknown` means the facts did not arrive, so the next step
// is usually the agent or the manifest, not the device. Collapsing them to one
// colour would erase the triage distinction — the same reasoning FD-197 applied to
// aborted/salvaged vs faulted in StatusBadge.
//
// There is deliberately NO neutral fallback for a real verdict value. A verdict
// falling through to grey is exactly the FD-197 bug (a faulted device rendering as
// a benign chip), so every ReadinessStatus is handled explicitly and only the
// genuinely-absent case is subtle.

const LABELS: Record<ReadinessStatus, string> = {
  pass: 'Ready',
  warn: 'Ready (warnings)',
  unknown: 'Unknown',
  blocked: 'Not ready',
};

export interface ReadinessBadgeProps {
  status: ReadinessStatus | null;
  /**
   * Whether an operator override stands on this device (FD-287).
   *
   * The badge KEEPS its verdict colour and label. An override releases the gate;
   * it does **not** rewrite the verdict (FD-ADR-027), and the failing reasons
   * remain the audit trail. What changes is that a released device must not look
   * identical to one that is genuinely held — otherwise an operator scanning the
   * grid chases a device that was dealt with days ago.
   */
  overridden?: boolean;
  // When the deployment is in `observe`, a blocking verdict is not actually holding
  // anything. The badge stays the same colour — the verdict is the verdict — but the
  // title says so, because a red chip that isn't stopping anything is misleading on
  // its own.
  enforcement?: 'observe' | 'enforce';
}

export default function ReadinessBadge({
  status,
  enforcement,
  overridden,
}: ReadinessBadgeProps) {
  if (status === null || status === undefined) {
    return (
      <Badge appearance="tint" color="subtle" title="No manifest submitted yet — readiness has not been evaluated">
        Not evaluated
      </Badge>
    );
  }

  const holds = status === 'blocked' || status === 'unknown';
  // An override wins the sentence: once one stands, the enforcement mode is no
  // longer the reason this device is or is not moving.
  const suffix = overridden
    ? ' — released by an operator override; the failing checks are still recorded'
    : holds && enforcement === 'observe'
      ? ' — observe mode, so the flight plan is not being withheld'
      : holds && enforcement === 'enforce'
        ? ' — the flight plan is being withheld'
        : '';

  // Rendered NEXT TO the verdict rather than replacing it, for the same reason:
  // the verdict is still true. Applied by `withOverride` at each return site so
  // that a device with no override keeps exactly the DOM it had before — the
  // common case must not change shape to serve the rare one.
  const withOverride = (badge: JSX.Element) =>
    overridden ? (
      <span className="inline-flex items-center gap-1.5">
        {badge}
        <Badge
          appearance="outline"
          color="informative"
          title="An operator override releases this device"
        >
          Overridden
        </Badge>
      </span>
    ) : (
      badge
    );

  switch (status) {
    case 'pass':
      return withOverride(
        <Badge appearance="tint" color="success" title={`Ready to migrate${suffix}`}>
          {LABELS.pass}
        </Badge>,
      );
    case 'warn':
      return withOverride(
        <Badge appearance="tint" color="informative" title={`Ready, with non-blocking warnings${suffix}`}>
          {LABELS.warn}
        </Badge>,
      );
    case 'unknown':
      return withOverride(
        <Badge appearance="tint" color="warning" title={`A required check could not be evaluated${suffix}`}>
          {LABELS.unknown}
        </Badge>,
      );
    case 'blocked':
      return withOverride(
        <Badge appearance="tint" color="danger" title={`A required check failed${suffix}`}>
          {LABELS.blocked}
        </Badge>,
      );
    default: {
      // Exhaustiveness guard: adding a ReadinessStatus without a case here is a
      // compile error rather than a silent grey chip.
      const exhaustive: never = status;
      return <Badge appearance="tint" color="subtle">{String(exhaustive)}</Badge>;
    }
  }
}
